import { twMerge } from 'tailwind-merge'
import { MessageFeedback } from '@renderer/components/feedback'
import { AmplitudeRenderer } from '@renderer/utils/amplitude.renderer'
import { useClaimFloatingBarCopy } from '../../../atoms'
import { BraintrustSpanLink } from '../../../components/braintrust-span-link'
import { useConversationBraintrustLink } from '../../../components/use-conversation-braintrust-link'
import { useCopyBrief } from '../../../components/meeting/brief/use-copy-brief'
import { BriefSummarySection } from '../../../components/meeting/brief/sections/brief-summary-section'
import { BriefLinksSection } from '../../../components/meeting/brief/sections/brief-links-section'
import { RegenerateBriefButton } from './brief-generation'
import type { MeetingViewModel } from '../use-meeting-view-model'

interface MeetingViewBriefBodyProps {
  /** The open tab, so only the panel on screen offers the bar its copy. */
  isActiveTab?: boolean
  model: MeetingViewModel
  className?: string
}

/**
 * The brief in full (HIG-5827): a feedback, regenerate, and copy row over the
 * brief and its links. No section header of its own. Before there's a recording
 * the header card is the brief's header; in the Brief tab, the tab names it.
 * The brief's generation chat is rated via its conversationId, and the server
 * resolves the terminal message.
 */
export function MeetingViewBriefBody({ model, className, isActiveTab = false }: MeetingViewBriefBodyProps) {
  const brief = model.brief
  if (!brief) {
    return null
  }

  return (
    // pt-0 so the brief flows straight out of the header card while it is the
    // body. The Brief tab passes its own top padding instead.
    <section aria-label="Brief" className={twMerge('flex flex-col gap-4 px-5 pb-5 pt-0', className)}>
      <BriefSummarySection markdown={brief.brief} structure={brief.structure} previews={brief.linkPreviews} />
      <BriefLinksSection links={brief.relevantLinks} previews={brief.linkPreviews} />
      <div className="flex items-center justify-between gap-2 empty:hidden">
        <div className="flex items-center gap-0.5">
          <MessageFeedback
            subject={{ conversationId: brief.conversationId }}
            source="meeting-brief"
            initialRating={model.briefFeedbackRating}
          />
          <MeetingBriefBraintrustLink conversationId={brief.conversationId} />
        </div>
        <div className="flex items-center gap-0.5">
          <RegenerateBriefButton model={model} />
          <BriefCopyClaim brief={brief.brief} active={isActiveTab} />
        </div>
      </div>
    </section>
  )
}

function MeetingBriefBraintrustLink({ conversationId }: { conversationId: string }) {
  const braintrustLink = useConversationBraintrustLink(conversationId)
  return braintrustLink ? <BraintrustSpanLink link={braintrustLink} /> : null
}

function BriefCopyClaim({ brief, active }: { brief: string; active: boolean }) {
  const { handleCopy, hasBrief } = useCopyBrief(brief)
  useClaimFloatingBarCopy(
    active &&
      hasBrief && {
        label: 'Copy brief',
        copy: () => {
          void handleCopy()
          AmplitudeRenderer.trackEvent('Audio Note Action', {
            action: 'copy brief',
            trigger: 'sidepanel meeting view',
          })
        },
      },
  )
  return null
}
